/* eslint-disable react/destructuring-assignment */
import { DataSource } from 'hooks/useHttpClient';
import {
    Address,
    AddressResolver,
    FactAudit,
    LegacyAddress,
    LegacyAddressResolver,
    PaginatedList,
    PaginationMeta,
    PaginationMetaResolver
} from './Common';
import { Job, JobSingleResolver } from './Job';
import { Document, DocumentListResolver } from './Document';

export type Resume = {
    url: string;
    source: 'hyrerocket' | 's3'; // Is gonna be insteresting how to get this.
};

export type BasicMappedStatus = {
    id: number;
    name: string;
};

export type SearchTags = {
    name: string;
    number: number;
};

export type EngingeSearchContext = {
    searchTags?: SearchTags[];
    similarity?: number;
    // TODO Add a better resolver for each engine
    engine: 'legacy' | 'sirius_v1';
};

export type CandidateAtJob = {
    id: number;
    jobStage: BasicMappedStatus;
    addedBy: FactAudit;
    testLink: string | null;
    testScore: number | null;
    candidate: Candidate;
    job: Job;
    // TODO Add more fields
};

export type CandidateDevice = {
    model?: string;
    modelName?: string;
    name?: string;
    serialNumber?: string;
    lastSeenAt?: string;
    location?: Record<string, any>;
};

export type Candidate = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    employeeEmail?: string;
    phoneNumber: string;
    resume: Resume;
    location: Address | null;
    legacyLocation: LegacyAddress | null;
    avatar: string | null;
    source: string; // TODO check values of this field
    sourceBy: FactAudit;
    status: BasicMappedStatus;
    candidateApplications: Omit<CandidateAtJob, 'candidate'>[];
    linkedinProfile?: string;
    socialLinks?: string[];
    tags?: string;
    customLabels?: string;
    engineSearchContext?: EngingeSearchContext;
    documents: Document[];
};

export const CandidateStatuses = [
    { id: 1, name: 'Passive' },
    { id: 2, name: 'Active' },
    { id: 3, name: 'Hired' },
    { id: 4, name: 'Do Not Hire' }
];

export const CandidateSources = [
    { id: 1, name: 'No Sourced' },
    { id: 2, name: 'Internal referral' },
    { id: 3, name: 'External referral' },
    { id: 4, name: 'Sourced' },
    { id: 5, name: 'Applied' }
];

// TODO Change this someday to pickup from the API
export const JobStages = [
    { id: 1, name: 'System AI' },
    { id: 2, name: 'Rejected' },
    { id: 3, name: 'Not Interested' },
    { id: 5, name: 'Sourced' },
    { id: 6, name: 'Active' },
    { id: 7, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Screen` },
    { id: 8, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Technical Screen` },
    { id: 9, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Technical Testing` },
    { id: 10, name: 'Company Scheduling' },
    { id: 11, name: 'Company Interviews' },
    { id: 12, name: 'Company Accepted' },
    { id: 13, name: 'Company Hired' },
    { id: 14, name: 'Onboard' },
    { id: 15, name: 'Withdrawn' },
    { id: 16, name: 'Offboarded' },
    { id: 17, name: 'Bench' }
];

export const PipelineStatuses = [
    { id: 1, name: 'System AI' },
    { id: 5, name: 'Sourced' },
    { id: 7, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Screen` },
    { id: 8, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Technical Screen` },
    { id: 9, name: `${process.env.REACT_APP_SHORT_COMPANY_PREFIX || ''} Technical Testing` },
    { id: 10, name: 'Company Scheduling' },
    { id: 11, name: 'Company Interviews' },
    { id: 12, name: 'Company Accepted' },
    { id: 13, name: 'Company Hired' },
    { id: 14, name: 'Onboard' }
];

export function CandidateSingeResolver(dataSource: DataSource, candidate: any, withApplications: Boolean): Candidate | null {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                const socialLinks: string[] = candidate.links?.map((link: any) => link.link) || [];
                return {
                    id: candidate.id,
                    firstName: candidate.first_name,
                    lastName: candidate.last_name,
                    email: candidate.email,
                    employeeEmail: candidate.employee_email,
                    phoneNumber: candidate.phone,
                    resume: {
                        source: 'hyrerocket',
                        url: candidate.resume
                    },
                    location: AddressResolver(dataSource, candidate.locationable),
                    legacyLocation: LegacyAddressResolver(dataSource, candidate.locationable),
                    status: {
                        id: candidate.status.id,
                        name: candidate.status.status
                    },
                    avatar: candidate.profile_picture,
                    source: candidate.source,
                    sourceBy: {
                        userId: candidate.source_by?.id || 0,
                        userFullName: candidate.source_by?.name,
                        avatar: candidate.source_by?.profile?.profile_picture || undefined,
                        dateStr: candidate.created_at
                    },
                    candidateApplications: withApplications
                        ? candidate.jobrequisitions.map((application: any) => ({
                              id: application.id,
                              jobStage: {
                                  id: application.stage.id,
                                  name: application.stage.name
                              },
                              addedBy: {
                                  userId: application.source_by || 0,
                                  userFullName: undefined, // 'Need to load entity',
                                  avatar: 'Need to load entity',
                                  dateStr: application.created_at
                              },
                              testLink: application.test_link,
                              testScore: application.test_score,
                              job: JobSingleResolver(dataSource, application.jobrequisition, false)
                          }))
                        : [],
                    linkedinProfile: socialLinks.find((st) => st.includes('linkedin')),
                    socialLinks,
                    tags: candidate.tags
                        ? candidate.tags
                              .map((t: any) => t.tag.trim())
                              .filter((t: any) => t !== '')
                              .join(',')
                        : undefined,
                    customLabels: candidate.customLabels
                        ? candidate.customLabels
                              .map((t: any) => t.tag.trim())
                              .filter((t: any) => t !== '')
                              .join(',')
                        : undefined,
                    engineSearchContext: candidate.searchSource
                        ? {
                              engine: candidate.searchSource,
                              searchTags: candidate.engineSearchContext.searchTags
                                  ? Object.keys(candidate.engineSearchContext.searchTags).map((st) => ({
                                        name: st,
                                        number: +candidate.engineSearchContext.searchTags[st]
                                    }))
                                  : undefined,
                              similarity: candidate.engineSearchContext.valid ? candidate.engineSearchContext.score : 0
                          }
                        : undefined,

                    documents: DocumentListResolver(dataSource, candidate.documents)
                };
            }

            default:
                return null;
        }
    } catch (error) {
        console.log('Candidate resolver error', error);
        return null;
    }
}

export function CandidateListResolver(dataSource: DataSource, apiResponse: any): PaginatedList<Candidate> | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    data: apiResponse.data
                        .map((candidate: any) => CandidateSingeResolver(dataSource, candidate, true))
                        .filter((candidate: Candidate | null) => !!candidate) as Candidate[],
                    meta: PaginationMetaResolver(dataSource, apiResponse.meta) as PaginationMeta
                };

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
}

export function CandidateApplicationResolver(dataSource: DataSource, candidate: any, withApplications: Boolean): Candidate | null {
    try {
        switch (dataSource) {
            case 'hyrerocket': {
                return {
                    id: candidate.id,
                    firstName: candidate.first_name,
                    lastName: candidate.last_name,
                    email: candidate.email,
                    phoneNumber: candidate.phone,
                    resume: {
                        source: 'hyrerocket',
                        url: candidate.resume
                    },
                    location: AddressResolver(dataSource, candidate.locationable),
                    legacyLocation: LegacyAddressResolver(dataSource, candidate.locationable),
                    status: {
                        id: candidate.status.id,
                        name: candidate.status.status
                    },
                    avatar: candidate.profile_picture,
                    source: candidate.source,
                    sourceBy: {
                        userId: candidate.source_by?.id || 0,
                        userFullName: candidate.source_by?.name,
                        avatar: candidate.source_by?.profile?.profile_picture || undefined,
                        dateStr: candidate.created_at
                    },
                    candidateApplications: [
                        {
                            id: candidate.job?.id,
                            jobStage: {
                                id: candidate.job?.stage?.id,
                                name: candidate.job?.stage?.name
                            },
                            addedBy: {
                                userId: candidate.source_by?.id || 0,
                                userFullName: candidate.source_by?.name, // 'Need to load entity',
                                avatar: candidate.source_by?.profile?.profile_picture || undefined,
                                dateStr: candidate.created_at
                            },
                            testLink: candidate.job.test_link,
                            testScore: candidate.job.test_score,
                            job: { id: candidate.job.job_requisition_id } as unknown as Job
                        }
                    ],
                    linkedinProfile: candidate.links ? candidate.links[0]?.link || undefined : undefined, // TODO check this
                    tags: candidate.tags
                        ? candidate.tags
                              .map((t: any) => t.tag.trim())
                              .filter((t: any) => t !== '')
                              .join(',')
                        : undefined,
                    engineSearchContext: candidate.searchSource
                        ? {
                              engine: candidate.searchSource,
                              searchTags: candidate.engineSearchContext.searchTags
                                  ? Object.keys(candidate.engineSearchContext.searchTags).map((st) => ({
                                        name: st,
                                        number: +candidate.engineSearchContext.searchTags[st]
                                    }))
                                  : undefined,
                              similarity: candidate.engineSearchContext.similarity
                          }
                        : undefined,
                    documents: DocumentListResolver(dataSource, candidate.documents)
                };
            }

            default:
                return null;
        }
    } catch (error) {
        console.log('Candidate application resolver error', error);
        return null;
    }
}

export function CandidateSingleDeviceResolver(dataSource: DataSource, apiResponse: any): CandidateDevice | null {
    if (!apiResponse) {
        return null;
    }
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    model: apiResponse.model,
                    modelName: apiResponse.model_name,
                    name: apiResponse.name,
                    serialNumber: apiResponse.serial_number,
                    lastSeenAt: apiResponse.last_seen_at,
                    location: JSON.parse(apiResponse.location)
                };

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
}
